import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { loginApi } from "../services/auth";
import { isLoggedIn, persistLogin } from "../helpers/local_storage_helpers";
import InputField from "../components/inputFields/InputField";
import {
    HiShieldCheck,
    HiArrowRight,
    HiUser,
    HiLockClosed,
    HiPhone,
    HiOfficeBuilding,
    HiArrowLeft,
    HiMail,
} from "react-icons/hi";
import { FaSeedling, FaUsers, FaChartBar, FaHandshake } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import logo from "../assets/icons/Rashail-agro-logo.png";

// ── Validation ────────────────────────────────────────────────────
const LoginSchema = Yup.object().shape({
    username: Yup.string()
        .min(2, "Username too short")
        .required("Username is required"),
    password: Yup.string()
        .min(4, "Password too short")
        .required("Password is required"),
});

const ForgotSchema = Yup.object().shape({
    fpo_name: Yup.string()
        .min(2, "FPO Name is required")
        .required("FPO Name is required"),
    name: Yup.string()
        .min(2, "Name is required")
        .required("Name is required"),
    mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required"),
});

// ── Feature list for right panel ──────────────────────────────────
const features = [
    { icon: FaUsers,     text: "Member management & onboarding" },
    { icon: FaSeedling,  text: "Crop & commodity tracking" },
    { icon: FaChartBar,  text: "Real-time financial analytics" },
    { icon: FaHandshake, text: "Buyer-seller facilitation" },
];

const Signin = () => {
    const navigate                = useNavigate();
    const [showPass, setShowPass]     = useState(false);
    const [mounted, setMounted]       = useState(false);
    const [isForgotOpen, setIsForgot] = useState(false);

    useEffect(() => {
        setMounted(true);
        if (isLoggedIn()) navigate("/dashboard");
    }, [navigate]);

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const response     = await loginApi(values);
            const responseData = response?.data;
            const apiStatus    = responseData?.status;
            const loginOk      = apiStatus === 200 || apiStatus === "200";

            if (response?.status === 200 && loginOk) {
                persistLogin({
                    token:       responseData.token,
                    user:        responseData.user,
                    fpo:         responseData.fpo,
                    role:        responseData.role,
                    designation: responseData.designation,
                });
                toast.success("Login successful! Welcome back 🌾", {
                    position:  "top-center",
                    autoClose: 1500,
                });
                resetForm();
                setTimeout(() => navigate("/dashboard"), 1600);
            } else {
                toast.error(responseData?.message || "Login failed. Please check your credentials.");
            }
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Something went wrong. Please try again.";
            toast.error(msg);
        }
        setSubmitting(false);
    };

    const handleForgotSubmit = (values, { setSubmitting }) => {
        const fpo_name = values.fpo_name || "Test FPO";
        const name     = values.name     || "Ashutosh";
        const mobile   = values.mobile   || "9407386981";

        const emailTo = "ashutosh@rashailinfotech.co.in";
        const subject = encodeURIComponent(`Password Reset Request - ${fpo_name}`);
        const body    = encodeURIComponent(
            `Dear Support Team,\n\nI am requesting a password reset for my FPO account.\n\nFPO Name: ${fpo_name}\nContact Name: ${name}\nMobile Number: ${mobile}\n\nPlease reset my credentials.\n\nThank you.`
        );

        const gmailUrl = `https://mail.google.com/mail/?authuser=${encodeURIComponent(emailTo)}&view=cm&fs=1&to=${emailTo}&su=${subject}&body=${body}`;

        toast.info("Opening Gmail compose... 📧", {
            position: "top-center",
            autoClose: 1500,
        });

        setTimeout(() => {
            window.location.href = gmailUrl;
        }, 300);

        setSubmitting(false);
    };

    // ── Animations ────────────────────────────────────────────────
    const containerVariants = {
        hidden:  { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.5 } },
    };

    const leftPanelVariants = {
        hidden:  { opacity: 0, x: -40 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
    };

    const rightPanelVariants = {
        hidden:  { opacity: 0, x: 40 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    };

    const itemVariants = {
        hidden:  { opacity: 0, y: 20 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.12 + 0.4, duration: 0.45 },
        }),
    };

    return (
        <AnimatePresence>
            {mounted && (
                <motion.div
                    key="login"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="min-h-screen flex"
                >
                    {/* ── LEFT PANEL — Login Form / Forgot Form ──────────── */}
                    <motion.div
                        variants={leftPanelVariants}
                        initial="hidden"
                        animate="visible"
                        className="w-full md:w-1/2 flex flex-col justify-between items-center px-6 sm:px-10 py-6 sm:py-8 bg-gradient-to-br from-white via-gray-50 to-green-50 min-h-screen"
                    >
                        {/* Company Logo at top */}
                        <div className="w-full flex justify-center pt-2 sm:pt-4">
                            <img
                                src={logo}
                                alt="Rashail Agro"
                                className="h-12 sm:h-14 object-contain cursor-pointer"
                                onClick={() => setIsForgot(false)}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = "none";
                                }}
                            />
                        </div>

                        {/* Main Login / Forgot Card */}
                        <div className="w-full max-w-sm flex flex-col items-center my-auto py-4">
                            {!isForgotOpen ? (
                                <>
                                    <motion.div
                                        custom={0}
                                        variants={itemVariants}
                                        className="text-center mb-6"
                                    >
                                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1a4a4a]">
                                            Welcome Back
                                        </h1>
                                        <p className="text-gray-500 text-sm mt-1">
                                            Sign in to access your FPO dashboard
                                        </p>
                                    </motion.div>

                                    {/* Sign In Form card */}
                                    <motion.div
                                        custom={1}
                                        variants={itemVariants}
                                        className="w-full bg-white rounded-2xl shadow-xl border border-gray-100 px-7 py-7"
                                    >
                                        {/* Secure badge */}
                                        <div className="flex items-center justify-center gap-2 text-gray-400 text-xs mb-5">
                                            <HiShieldCheck className="text-primary text-base" />
                                            <span>Secure Sign In</span>
                                        </div>

                                        <Formik
                                            initialValues={{ username: "", password: "" }}
                                            validationSchema={LoginSchema}
                                            onSubmit={handleSubmit}
                                        >
                                            {({ isSubmitting }) => (
                                                <Form className="flex flex-col gap-4">
                                                    {/* Username */}
                                                    <InputField
                                                        name="username"
                                                        type="text"
                                                        label="Username"
                                                        required
                                                        leftIcon={HiUser}
                                                        placeholder="Enter your username"
                                                    />

                                                    {/* Password */}
                                                    <div>
                                                        <InputField
                                                            name="password"
                                                            label="Password"
                                                            required
                                                            leftIcon={HiLockClosed}
                                                            isPassword
                                                            showPassword={!showPass}
                                                            onPasswordToggle={() => setShowPass((p) => !p)}
                                                            placeholder="Enter your password"
                                                        />
                                                        <div className="flex justify-end mt-1.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => setIsForgot(true)}
                                                                className="text-xs text-primary font-semibold hover:underline focus:outline-none"
                                                            >
                                                                Forgot Password?
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Submit */}
                                                    <motion.button
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className="w-full mt-2 py-3 px-6 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-primary/25 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                                </svg>
                                                                Signing in...
                                                            </>
                                                        ) : (
                                                            <>
                                                                Sign In to Dashboard
                                                                <HiArrowRight className="text-lg" />
                                                            </>
                                                        )}
                                                    </motion.button>
                                                </Form>
                                            )}
                                        </Formik>

                                        <p className="text-center text-xs text-gray-400 mt-5">
                                            Protected by enterprise-grade security
                                        </p>
                                    </motion.div>
                                </>
                            ) : (
                                <>
                                    <motion.div
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-center mb-6"
                                    >
                                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1a4a4a]">
                                            Forgot Password?
                                        </h1>
                                        <p className="text-gray-500 text-sm mt-1">
                                            Fill details to request a password reset via email
                                        </p>
                                    </motion.div>

                                    {/* Forgot Password Form card */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="w-full bg-white rounded-2xl shadow-xl border border-gray-100 px-7 py-7"
                                    >
                                        <Formik
                                            initialValues={{ fpo_name: "", name: "", mobile: "" }}
                                            validationSchema={ForgotSchema}
                                            onSubmit={handleForgotSubmit}
                                        >
                                            {({ isSubmitting }) => (
                                                <Form className="flex flex-col gap-4">
                                                    {/* FPO Name */}
                                                    <InputField
                                                        name="fpo_name"
                                                        type="text"
                                                        label="FPO Name"
                                                        required
                                                        leftIcon={HiOfficeBuilding}
                                                        placeholder="Enter your FPO name"
                                                    />

                                                    {/* Contact Name */}
                                                    <InputField
                                                        name="name"
                                                        type="text"
                                                        label="Your Name"
                                                        required
                                                        leftIcon={HiUser}
                                                        placeholder="Enter your full name"
                                                    />

                                                    {/* Mobile Number */}
                                                    <InputField
                                                        name="mobile"
                                                        type="tel"
                                                        label="Mobile Number"
                                                        required
                                                        leftIcon={HiPhone}
                                                        placeholder="10-digit mobile number"
                                                        maxLength={10}
                                                    />

                                                    {/* Submit / Mail button */}
                                                    <motion.button
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className="w-full mt-2 py-3 px-6 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-primary/25 flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                                    >
                                                        <HiMail className="text-lg" />
                                                        Send Reset Request
                                                    </motion.button>

                                                    {/* Back button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsForgot(false)}
                                                        className="w-full py-2.5 px-4 text-xs font-semibold text-gray-500 hover:text-gray-800 flex items-center justify-center gap-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors mt-1"
                                                    >
                                                        <HiArrowLeft className="text-sm" />
                                                        Back to Sign In
                                                    </button>
                                                </Form>
                                            )}
                                        </Formik>

                                        <p className="text-center text-[11px] text-gray-400 mt-4 leading-relaxed">
                                            This will open Gmail in your browser to send request to{" "}
                                            <span className="font-semibold text-gray-600">ashutosh@rashailinfotech.co.in</span>
                                        </p>
                                    </motion.div>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="pb-2 text-center">
                            <p className="text-xs text-gray-400">
                                Powered by{" "}
                                <a
                                    href="https://rashailagro.in/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary font-semibold hover:underline"
                                >
                                    Rashail Agro
                                </a>
                            </p>
                        </div>
                    </motion.div>

                    {/* ── RIGHT PANEL — Illustration ───────────────────────── */}
                    <motion.div
                        variants={rightPanelVariants}
                        initial="hidden"
                        animate="visible"
                        className="hidden md:flex w-1/2 bg-gradient-to-br from-[#e8f5e9] via-[#c8e6c9] to-[#e0f2f1] flex-col justify-center items-center px-10 py-12 relative overflow-hidden"
                    >
                        {/* Secure Login badge */}
                        <div className="absolute top-6 right-6">
                            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow text-sm font-semibold text-[#1a4a4a] border border-white">
                                <MdVerified className="text-primary text-base" />
                                Secure Login
                            </div>
                        </div>

                        {/* Central illustration */}
                        <div className="relative flex flex-col items-center">
                            {/* Decorative circles */}
                            <div className="absolute -top-16 -left-16 w-48 h-48 bg-primary/10 rounded-full" />
                            <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-green-200/40 rounded-full" />

                            {/* FPO Illustration Card */}
                            <motion.div
                                animate={{ y: [0, -12, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                className="relative z-10 bg-white rounded-3xl shadow-2xl p-6 w-64 border border-green-100"
                            >
                                {/* App mockup header */}
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                        <FaSeedling className="text-white text-sm" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-[#1a4a4a]">FPO Dashboard</p>
                                        <p className="text-[10px] text-gray-400">Admin Portal</p>
                                    </div>
                                </div>

                                {/* Stat rows */}
                                {[
                                    { label: "Members",     value: "248",  color: "bg-green-500" },
                                    { label: "Revenue",     value: "₹4.2L", color: "bg-blue-500" },
                                    { label: "Active Crops", value: "12",  color: "bg-amber-500" },
                                ].map((stat) => (
                                    <div key={stat.label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${stat.color}`} />
                                            <span className="text-xs text-gray-600">{stat.label}</span>
                                        </div>
                                        <span className="text-xs font-bold text-gray-800">{stat.value}</span>
                                    </div>
                                ))}

                                {/* Mini bar chart */}
                                <div className="mt-4 flex items-end gap-1.5 h-12">
                                    {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ scaleY: 0 }}
                                            animate={{ scaleY: 1 }}
                                            transition={{ delay: i * 0.08 + 0.5, duration: 0.4 }}
                                            style={{ height: `${h}%`, transformOrigin: "bottom" }}
                                            className={`flex-1 rounded-t-sm ${i === 5 ? "bg-primary" : "bg-primary/30"}`}
                                        />
                                    ))}
                                </div>
                            </motion.div>
                        </div>

                        {/* Text & features */}
                        <div className="mt-10 text-center max-w-sm z-10">
                            <h2 className="text-2xl font-bold text-[#1a4a4a] mb-2">
                                Manage Your FPO
                            </h2>
                            <p className="text-gray-600 text-sm mb-6">
                                Access powerful tools to manage members, track commodities,
                                and grow your Farmer Producer Organization.
                            </p>

                            <div className="space-y-3 text-left">
                                {features.map((f, i) => (
                                    <motion.div
                                        key={i}
                                        custom={i}
                                        variants={itemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                            <f.icon className="text-white text-[10px]" />
                                        </div>
                                        <span className="text-sm text-gray-700">{f.text}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Signin;
